import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardHeader,
    Container,
    Divider,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Formik } from 'formik';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import UserDialog from '../Components/UserDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import { BRAND } from '../constants/brand';

const EXCLUDED_USERNAME = 'kanishka';

function getRoleLabel(role) {
    return role === 1 ? 'Admin' : role === 2 ? 'Cashier' : role === 3 ? 'Accountant' : 'Store Keeper';
}

function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}

TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
};

function UserManagement() {
    const [pageTitle] = useState(`${BRAND.name} User Management`);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [itemSearch, setItemSearch] = useState('');
    const [updateID, setUpdateID] = useState(0);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);

    const getAllUsers = useCallback(() => {
        setLoading(true);
        axios.get('https://serveravotasautofleet.ccsoftwares.com/Users/getUser').then((res) => {
            const list = Array.isArray(res.data) ? res.data : [];
            setUsers(list);
        }).catch(() => {
            setUsers([]);
            toast.error('Unable to fetch users');
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (!dialogOpen) {
            getAllUsers();
        }
    }, [dialogOpen, updateID, getAllUsers]);

    useEffect(() => {
        setPage(0);
    }, [itemSearch]);

    const filteredUsers = useMemo(() => {
        const term = itemSearch.toLowerCase().trim();
        return users.filter((user) => {
            const name = (user.Name || '').toString();
            const username = (user.username || '').toString();
            const email = (user.email || '').toString();

            const isExcluded =
                name.toLowerCase().trim() === EXCLUDED_USERNAME ||
                username.toLowerCase().trim() === EXCLUDED_USERNAME;

            if (isExcluded) {
                return false;
            }

            if (!term) {
                return true;
            }

            return (
                name.toLowerCase().includes(term) ||
                username.toLowerCase().includes(term) ||
                email.toLowerCase().includes(term) ||
                getRoleLabel(user.role).toLowerCase().includes(term)
            );
        });
    }, [users, itemSearch]);

    const paginatedUsers =
        rowsPerPage > 0
            ? filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : filteredUsers;

    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredUsers.length) : 0;

    useEffect(() => {
        if (rowsPerPage <= 0) {
            return;
        }
        const maxPage = Math.max(0, Math.ceil(filteredUsers.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [filteredUsers.length, page, rowsPerPage]);

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function handleClick() {
        setDialogOpen(true);
    }

    function deleteUser(id) {
        axios.delete(`https://serveravotasautofleet.ccsoftwares.com/Users/deleteUser/${id}`).then(() => {
            toast.success('User Deleted');
            getAllUsers();
        }).catch(() => {
            toast.error('Unable to delete user');
        });
    }

    function cardTitle(titleName) {
        return (
            <Grid container spacing={1}>
                <Grid item md={11} xs={11}>
                    {titleName}
                </Grid>
                <Grid item md={1} xs={1}>
                    <Box display='flex' justifyContent='flex-end' alignItems='center' >
                        <Button variant="contained" endIcon={<AddCircleOutlineIcon />} onClick={() => handleClick()} sx={{ width: '100%' }}>
                            Add
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        );
    }

    return (
        <>
            <Container maxWidth={false} sx={{ paddingTop: '15vh' }}>
                <Formik enableReinitialize>
                    {() => (
                        <form>
                            <Box mt={0}>
                                <Card sx={{ padding: '15px' }}>
                                    <CardHeader title={cardTitle(pageTitle)} />
                                    <Divider />
                                    <br />
                                    <TableContainer>
                                        <Grid container spacing={2}>
                                            <Grid item md={6} sx={6}>
                                                <Box sx={{ display: 'flex', justifyContent: 'start' }}>
                                                    <SearchIcon sx={{ color: 'action.active', mr: 1, my: 1 }} />
                                                    <TextField
                                                        sx={{ width: { md: '50%', xs: '60%' } }}
                                                        variant="outlined"
                                                        label="Search by name, email or role"
                                                        value={itemSearch}
                                                        onChange={(e) => { setItemSearch(e.target.value); }}
                                                        size='small'
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <br />
                                        <Table sx={{ minWidth: 500 }} size="small" aria-label="user table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }}>#</TableCell>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }}>User Name</TableCell>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }}>User Email</TableCell>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }}>User Role</TableCell>
                                                    <TableCell align='center' sx={{ fontWeight: 'bold' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {loading ? (
                                                    <TableRow>
                                                        <TableCell align='center' colSpan={5}>
                                                            <Typography color="text.secondary">Loading users...</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : paginatedUsers.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell align='center' colSpan={5}>
                                                            <Typography color="text.secondary">No records to display!</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    paginatedUsers.map((row, index) => (
                                                        <TableRow key={row.userkey || row._id || index}>
                                                            <TableCell align='center'>{index + 1 + page * rowsPerPage}</TableCell>
                                                            <TableCell align='center'>{row.Name || row.username || '-'}</TableCell>
                                                            <TableCell align='center'>{row.email || '-'}</TableCell>
                                                            <TableCell align='center'>{getRoleLabel(row.role)}</TableCell>
                                                            <TableCell align='center'>
                                                                <IconButton>
                                                                    <DeleteIcon sx={{ color: 'black' }} onClick={() => deleteUser(row.userkey)} />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                                {emptyRows > 0 && !loading && (
                                                    <TableRow style={{ height: 53 * emptyRows }}>
                                                        <TableCell colSpan={5} />
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                            <TableFooter>
                                                <TableRow>
                                                    <TablePagination
                                                        rowsPerPageOptions={[10, 25, 35, { label: 'All', value: -1 }]}
                                                        colSpan={5}
                                                        count={filteredUsers.length}
                                                        rowsPerPage={rowsPerPage}
                                                        page={page}
                                                        slotProps={{
                                                            select: {
                                                                inputProps: {
                                                                    'aria-label': 'rows per page',
                                                                },
                                                                native: true,
                                                            },
                                                        }}
                                                        onPageChange={handleChangePage}
                                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                                        ActionsComponent={TablePaginationActions}
                                                    />
                                                </TableRow>
                                            </TableFooter>
                                        </Table>
                                    </TableContainer>
                                </Card>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Container>

            <UserDialog
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                updateID={updateID}
                setUpdateID={setUpdateID}
            />
        </>
    );
}

export default UserManagement;
